import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';

export default function Footer({ darkMode }) {
  const { t } = useTranslation();

  const bg      = darkMode ? '#0a0a0f' : '#f1f3f9';
  const border  = darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)';
  const text    = darkMode ? 'rgba(255,255,255,0.85)' : '#1a1a2e';
  const muted   = darkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const accent  = '#8b5cf6';

  const linkStyle = {
    color: muted,
    textDecoration: 'none',
    fontSize: 13,
    transition: 'color 0.2s',
  };

  return (
    <footer style={{ background: bg, borderTop: `1px solid ${border}`, color: text }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px 20px' }}>

        {/* Main grid: brand + 3 link columns */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '24px 32px',
          marginBottom: 24,
        }}>

          {/* Brand */}
          <div style={{ gridColumn: 'span 2' }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6, color: text }}>
              DerrDigital
            </div>
            <p style={{ color: muted, fontSize: 12, lineHeight: 1.6, marginBottom: 14, maxWidth: 220 }}>
              {t('footer.tagline')}
            </p>
            {/* Social icons */}
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { label: 'Facebook', href: 'https://facebook.com', path: 'M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z' },
                { label: 'Instagram', href: 'https://instagram.com', path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z' },
                { label: 'LinkedIn', href: 'https://linkedin.com', path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 23.2 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
              ].map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                  style={{ color: muted, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 6, border: `1px solid ${border}`, transition: 'color 0.2s, border-color 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.color = accent; e.currentTarget.style.borderColor = accent + '60'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = muted; e.currentTarget.style.borderColor = border; }}
                >
                  <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24"><path d={s.path} /></svg>
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <div style={{ fontWeight: 700, fontSize: 12, color: text, marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Navigation</div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[
                { to: '/#leistungen', label: t('nav.services') },
                { to: '/#warum-wir',  label: t('nav.whyUs') },
                { to: '/shop',        label: 'Shop' },
                { to: '/kontakt',     label: t('nav.contact') },
                { to: '/preview',     label: 'Dein Design' },
              ].map(l => (
                <li key={l.to}><Link to={l.to} style={linkStyle}
                  onMouseEnter={e => e.currentTarget.style.color = accent}
                  onMouseLeave={e => e.currentTarget.style.color = muted}>{l.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Unternehmen */}
          <div>
            <div style={{ fontWeight: 700, fontSize: 12, color: text, marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{t('footer.company')}</div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[
                { to: '/about', label: t('footer.aboutUs') },
                { to: '/blog',  label: t('footer.blog') },
                { to: '/academy', label: t('footer.academy') },
                { to: '/shop',  label: 'Shop' },
              ].map(l => (
                <li key={l.to}><Link to={l.to} style={linkStyle}
                  onMouseEnter={e => e.currentTarget.style.color = accent}
                  onMouseLeave={e => e.currentTarget.style.color = muted}>{l.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Kontakt */}
          <div>
            <div style={{ fontWeight: 700, fontSize: 12, color: text, marginBottom: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{t('footer.contact')}</div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <li>
                <a href="mailto:info@derrdigital.de" style={{ ...linkStyle, display: 'flex', alignItems: 'center', gap: 6 }}
                  onMouseEnter={e => e.currentTarget.style.color = accent}
                  onMouseLeave={e => e.currentTarget.style.color = muted}>
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                  info@derrdigital.de
                </a>
              </li>
              <li>
                <a href="tel:+4917416280040" style={{ ...linkStyle, display: 'flex', alignItems: 'center', gap: 6 }}
                  onMouseEnter={e => e.currentTarget.style.color = accent}
                  onMouseLeave={e => e.currentTarget.style.color = muted}>
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                  +49 174 1628004
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: `1px solid ${border}`, paddingTop: 14, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ color: muted, fontSize: 11 }}>
            © {new Date().getFullYear()} DerrDigital. {t('footer.allRights')}
            {process.env.REACT_APP_VERSION && <span style={{ marginLeft: 8, opacity: 0.4 }}>v{process.env.REACT_APP_VERSION}</span>}
          </span>
          <div style={{ display: 'flex', gap: 16 }}>
            {[
              { to: '/impressum', label: t('footer.imprint') },
              { to: '/datenschutz', label: t('footer.privacy') },
              { to: '/agb', label: t('footer.agb') },
            ].map(l => (
              <Link key={l.to} to={l.to} style={{ ...linkStyle, fontSize: 11 }}
                onMouseEnter={e => e.currentTarget.style.color = accent}
                onMouseLeave={e => e.currentTarget.style.color = muted}>{l.label}</Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
